/**
 * Approval Navigation Hook
 *
 * Provides navigation between affected sections during approval phase
 * Similar to useNoteTOC but focused on affected sections only
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { LAYOUT_DIMENSIONS } from '../constants/layout';

export interface UseApprovalNavigationReturn {
  /** Currently focused affected section index (0-based) */
  currentIndex: number;
  /** Total number of affected sections */
  totalCount: number;
  /** Whether currently at first affected section */
  isFirst: boolean;
  /** Whether currently at last affected section */
  isLast: boolean;
  /** Scroll to first affected section */
  scrollToFirst: () => void;
  /** Scroll to next affected section (circular) */
  scrollToNext: () => void;
  /** Scroll to previous affected section (circular) */
  scrollToPrevious: () => void;
  /** Scroll to specific affected section by index */
  scrollToIndex: (index: number) => void;
}

/**
 * Hook for navigating between affected sections during approval
 *
 * Features:
 * - Auto-scrolls to first affected section on mount
 * - Circular navigation (wraps at boundaries)
 * - Smooth scroll with offset for sticky headers
 * - Supports custom scrollable containers
 *
 * @param affectedSectionIds - Array of section IDs that need review
 * @param containerSelector - Optional CSS selector for scrollable container
 * @returns Navigation state and control functions
 */
export const useApprovalNavigation = (
  affectedSectionIds: string[],
  containerSelector?: string,
): UseApprovalNavigationReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasAutoScrolled = useRef(false);

  const totalCount = affectedSectionIds.length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalCount - 1;

  /**
   * Scroll to a specific section by section ID
   */
  const scrollToSection = useCallback(
    (sectionId: string) => {
      // Find the section element by data attribute
      const element = document.querySelector(`[data-section-id="${sectionId}"]`);

      if (element) {
        // Get the scrollable container or use window
        const container = containerSelector ? document.querySelector(containerSelector) : null;

        if (container) {
          // Calculate scroll position relative to container
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const scrollTop = container.scrollTop;

          // Account for sticky headers: TopBar + metadata + spacing

          // Element's current position in scrollable area
          const offset = elementRect.top - containerRect.top + scrollTop;

          container.scrollTo({
            top: offset - LAYOUT_DIMENSIONS.SCROLL_OFFSET,
            behavior: 'smooth',
          });
        } else {
          // Window scroll fallback - center in viewport
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }
    },
    [containerSelector],
  );

  /**
   * Scroll to first affected section
   */
  const scrollToFirst = useCallback(() => {
    if (totalCount > 0) {
      setCurrentIndex(0);
      scrollToSection(affectedSectionIds[0]);
    }
  }, [affectedSectionIds, totalCount, scrollToSection]);

  /**
   * Scroll to next affected section (circular)
   */
  const scrollToNext = useCallback(() => {
    if (totalCount === 0) return;

    const nextIndex = (currentIndex + 1) % totalCount;
    setCurrentIndex(nextIndex);
    scrollToSection(affectedSectionIds[nextIndex]);
  }, [affectedSectionIds, currentIndex, totalCount, scrollToSection]);

  /**
   * Scroll to previous affected section (circular)
   */
  const scrollToPrevious = useCallback(() => {
    if (totalCount === 0) return;

    const prevIndex = currentIndex === 0 ? totalCount - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    scrollToSection(affectedSectionIds[prevIndex]);
  }, [affectedSectionIds, currentIndex, totalCount, scrollToSection]);

  /**
   * Scroll to specific index
   */
  const scrollToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalCount) {
        setCurrentIndex(index);
        scrollToSection(affectedSectionIds[index]);
      }
    },
    [affectedSectionIds, totalCount, scrollToSection],
  );

  /**
   * Auto-scroll to first affected section when approval phase begins
   * Only runs once per approval session
   */
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (totalCount > 0 && !hasAutoScrolled.current) {
      // Set flag immediately to prevent race condition on fast updates
      hasAutoScrolled.current = true;

      // Small delay to ensure DOM is ready
      timer = setTimeout(() => {
        scrollToFirst();
      }, 300);
    }

    // Reset auto-scroll flag when affected sections change
    if (totalCount === 0) {
      hasAutoScrolled.current = false;
      setCurrentIndex(0);
    }

    // Always return cleanup to ensure timer is cleared
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [totalCount, scrollToFirst]);

  return {
    currentIndex,
    totalCount,
    isFirst,
    isLast,
    scrollToFirst,
    scrollToNext,
    scrollToPrevious,
    scrollToIndex,
  };
};
